import { type FC, useMemo } from 'react'
import { Loader } from '@mantine/core'
import { PhotoOwnerCaption } from '@/entities/Photos'
import { useEditPhotoWithPolygons } from '../lib/use-edit-photo-with-polygons'
import { Form, Formik, getIn } from 'formik'
import { PhotoPolygonEditor } from '@/features/Photos/EditPhotoPolygonsModal/ui/PhotoPolygonEditor'
import { updatePhotoPolygonsSchema } from '../lib/сonsts'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/Button/Button'
import type { Polygon } from '../model/types'

type EditPhotoPolygonsProps = {
  selectedPhotoId: number
  queryKeyForObjectUpdate: string
  obj_id: number
  owner: PhotoOwnerCaption
  buildings?: { id: number; name: string }[]
  onCloseModal: () => void
}

export const EditPhotoPolygons: FC<EditPhotoPolygonsProps> = props => {
  const { t } = useTranslation(['photo'])
  const {
    selectedPhotoId,
    queryKeyForObjectUpdate,
    obj_id,
    owner,
    buildings = [],
    onCloseModal,
  } = props

  const { currentPhoto, isLoadingPhoto, polygons, isPendingUpdate, submitFormPolygons } =
    useEditPhotoWithPolygons(selectedPhotoId, queryKeyForObjectUpdate, onCloseModal)

  const imageUrl = useMemo(() => {
    return `https://storage.googleapis.com/topli-public/${owner}/${obj_id}/${currentPhoto?.id}.webp?v=${currentPhoto?.created_at}`
  }, [currentPhoto, owner, obj_id])

  return (
    <div className='flex flex-col w-full h-full'>
      {isLoadingPhoto && (
        <div className='flex flex-col w-full min-h-[21.625rem] justify-center items-center'>
          <Loader size='sm' color='blue' />
        </div>
      )}

      {!isLoadingPhoto && (
        <Formik<Polygon[]>
          initialValues={polygons}
          validationSchema={updatePhotoPolygonsSchema(t)}
          enableReinitialize
          onSubmit={submitFormPolygons}
        >
          {({ values, errors, submitCount, setValues }) => {
            return (
              <Form>
                <PhotoPolygonEditor
                  imageUrl={imageUrl}
                  initialPolygons={polygons}
                  buildings={buildings}
                  onPolygonsChange={next => setValues(next, true)}
                  getBuildingError={polygonId => {
                    if (submitCount === 0) return null
                    const idx = values.findIndex(p => p.id === polygonId)
                    if (idx < 0) return null
                    const err = getIn(errors, `[${idx}].building_id`)
                    return err ? String(err) : null
                  }}
                />

                <div className='flex flex-row w-full pt-6 justify-start mt-10'>
                  <Button type='submit' size='md' color='base' disabled={isPendingUpdate}>
                    {isPendingUpdate ? (
                      <Loader size='xs' color='white' />
                    ) : (
                      t('photo:buttons.save_changes')
                    )}
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </div>
  )
}
